import React, { useEffect, useState } from "react";

const RoleAccessSelectorAdmin = ({
  modulesData = [],
  accessTypes = [],
  initialAccess = {},
  onChange = () => { },
  readOnly = false,
}) => {
  const [access, setAccess] = useState({});
  const viewActionId =
    accessTypes.find((a) => a.name.toUpperCase() === "VIEW")?._id || null;

  // ---------------------------------
  // Build modules grouped by their "module" (parentId)
  // ---------------------------------
  const buildGroupedModules = () => {
    const parentMap = {};

    modulesData.forEach((mod) => {
      const parentId = mod.module || mod._id; // parentId is from "module"
      if (!parentMap[parentId])
        parentMap[parentId] = { parentId, parentTitle: mod.title, children: [] };

      // Add module as child or merge its children
      if (mod.children && mod.children.length > 0) {
        parentMap[parentId].children.push(...mod.children);
      } else {
        parentMap[parentId].children.push(mod);
      }

    });

    return Object.values(parentMap);
  };

  const groupedModules = buildGroupedModules();

  // ---------------------------------
  // Initialize access state
  // ---------------------------------
  useEffect(() => {
    const defaultAccess = {};
    groupedModules.forEach(({ parentId, children }) => {
      defaultAccess[parentId] = {};
      children.forEach((child) => {
        defaultAccess[parentId][child._id] =
          initialAccess?.[parentId]?.[child._id] || [];
      });
    });
    setAccess(defaultAccess);
  }, [modulesData, initialAccess]);

  // ---------------------------------
  // Emit only selected (non-empty) access
  // ---------------------------------
  const emitFilteredAccess = (data) => {
    const filtered = {};
    Object.entries(data).forEach(([parentId, children]) => {
      const valid = Object.entries(children).reduce((acc, [childId, actions]) => {
        if (actions.length > 0) acc[childId] = actions;
        return acc;
      }, {});
      if (Object.keys(valid).length > 0) filtered[parentId] = valid;
    });
    onChange(filtered);
  };

  const updateAccess = (updated) => {
    setAccess(updated);
    emitFilteredAccess(updated);
  };

  // ---------------------------------
  // Toggle single action
  // ---------------------------------
  const handleToggle = (parentId, childId, actionId) => {
    if (readOnly) return;
    const current = access[parentId]?.[childId] || [];
    let newAccess;

    if (current.includes(actionId)) {
      newAccess = current.filter((a) => a !== actionId);
    } else {
      newAccess = [...current, actionId];
      if (
        viewActionId &&
        actionId !== viewActionId &&
        !newAccess.includes(viewActionId)
      ) {
        newAccess.push(viewActionId);
      }
    }

    const updated = {
      ...access,
      [parentId]: { ...access[parentId], [childId]: newAccess },
    };
    updateAccess(updated);
  };

  // ---------------------------------
  // Select/Deselect all for a child
  // ---------------------------------
  const handleSelectAll = (parentId, childId) => {
    if (readOnly) return;
    const allIds = accessTypes.map((a) => a._id);
    const isAllSelected =
      access[parentId]?.[childId]?.length === accessTypes.length &&
      accessTypes.every((a) => access[parentId]?.[childId]?.includes(a._id));

    const updated = {
      ...access,
      [parentId]: {
        ...access[parentId],
        [childId]: isAllSelected ? [] : allIds,
      },
    };
    updateAccess(updated);
  };

  const renderActionsRow = (parentId, childId) => (
    <div className="d-flex flex-wrap align-items-center gap-3 py-2">
      {accessTypes.map((act) => (
        <div className="form-check form-check-inline" key={act._id}>
          <input
            className="form-check-input"
            type="checkbox"
            disabled={readOnly}
            checked={access[parentId]?.[childId]?.includes(act._id) || false}
            onChange={() => handleToggle(parentId, childId, act._id)}
            id={`${parentId}-${childId}-${act._id}`}
          />
          <label
            htmlFor={`${parentId}-${childId}-${act._id}`}
            className="form-check-label small text-muted"
          >
            {act.name}
          </label>
        </div>
      ))}
    </div>
  );

  const renderHeaderRow = (parentId, title, childId) => {
    const isAllSelected =
      access[parentId]?.[childId]?.length === accessTypes.length &&
      accessTypes.every((a) => access[parentId]?.[childId]?.includes(a._id));

    return (
      <div className="d-flex justify-content-between align-items-center bg-light border-bottom px-3 py-2 rounded-top">
        <h6 className="fw-semibold mb-0 fs-7 text-dark">{title}</h6>
        <div className="form-check mb-0">
          <input
            className="form-check-input"
            type="checkbox"
            disabled={readOnly}
            checked={isAllSelected}
            onChange={() => handleSelectAll(parentId, childId)}
            id={`selectall-${parentId}-${childId}`}
          />
          <label
            htmlFor={`selectall-${parentId}-${childId}`}
            className="form-check-label small text-primary fw-bold"
          >
            Select All
          </label>
        </div>
      </div>
    );
  };

  // ---------------------------------
  // Render grouped modules (2 per row)
  // ---------------------------------
  const renderModulesGrid = () => {
    const rows = [];
    for (let i = 0; i < groupedModules.length; i += 2) {
      const pair = groupedModules.slice(i, i + 2);
      rows.push(
        <div className="row" key={i}>
          {pair.map((group) => (

            <div className="col-md-6" key={group.parentId}>
              <div className="card mb-3 shadow-sm border-primary">
                <div className="card-header bg-primary text-white fw-semibold fs-6 py-2">
                  {group.parentTitle || "Unnamed Module"}
                </div>
                <div className="card-body roleModule">
                  {group.children.map((child) => (
                    <div className="border rounded mb-3" key={child._id}>
                      {renderHeaderRow(group.parentId, child.title, child._id)}
                      <div className="px-3 noGap">
                        {renderActionsRow(group.parentId, child._id)}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>
      );
    }
    return rows;
  };

  return <div className="container-fluid">{renderModulesGrid()}</div>;
};

export default RoleAccessSelectorAdmin;
